// blog-posts.ts — single source of truth for the PrintPartyHub blog.
//
// Lives under src/seo/ so the SERVER can read it too (per-page <head> injection,
// src/seo/routes.ts). The frontend re-exports it from
// frontend/src/pages/blog/posts.ts; the RSS feed and sitemap scripts read it
// through that re-export. Never duplicate post metadata across files.
//
// Spec: ~/workspace/shared/blog-template/README.md
//
// Sort order at render time: newest first by `published_date`.

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  publishedDate: string; // YYYY-MM-DD
  updatedDate?: string;  // YYYY-MM-DD — only set on material content changes
  tags: string[];        // long-tail keyword phrases, not abstract categories
  readTime: string;      // "6 min read"
  author: string;        // "PrintPartyHub" for org-bylined posts
  heroImage?: string;    // relative path, e.g. "/blog/<slug>/hero.png" — falls back to og default
};

export const SITE_URL = "https://printpartyhub.com";
export const DEFAULT_OG_IMAGE = "https://printpartyhub.com/images/og-image.png";

export const POSTS: BlogPost[] = [
  // 2026-06-15 batch — new long-tail posts demonstrating the blog-template system
  {
    slug: "baby-shower-bingo-printable",
    title: "Baby Shower Bingo: How to Run It So Guests Actually Play",
    description: "A real planner's guide to baby shower bingo — what goes on the cards, when to hand them out, and the gift-opening variation that keeps every guest engaged.",
    publishedDate: "2026-06-15",
    tags: ["baby shower bingo", "baby shower games", "printable baby shower"],
    readTime: "7 min read",
    author: "PrintPartyHub",
  },
  {
    slug: "office-holiday-party-games-printable",
    title: "Office Holiday Party Games That Don't Make People Cringe",
    description: "Printable games that work for a 30-person office holiday party without forcing anyone to hug a coworker. Field-tested formats, prize ideas, and what to skip.",
    publishedDate: "2026-06-15",
    tags: ["office holiday party games", "printable office games", "corporate party games"],
    readTime: "8 min read",
    author: "PrintPartyHub",
  },
  {
    slug: "wedding-mad-libs-for-guests",
    title: "Wedding Mad Libs: Reception Tables, Advice Cards, and Real Templates",
    description: "How couples use printable mad libs at the reception — table-by-table mechanics, wording that gets real laughs, and the advice-card variant that doubles as a keepsake.",
    publishedDate: "2026-06-15",
    tags: ["wedding mad libs", "wedding reception games", "printable wedding games"],
    readTime: "6 min read",
    author: "PrintPartyHub",
  },

  // Legacy posts (2026-04-09 batch) — left in place; can be migrated to the new layout incrementally.
  {
    slug: "birthday-party-bingo-ideas",
    title: "10 Birthday Party Bingo Ideas Kids Will Love",
    description: "From superhero themes to backyard bingo, here are ten ways to make birthday party bingo the highlight of any kids' celebration.",
    publishedDate: "2026-04-09",
    tags: ["birthday party bingo", "bingo for kids"],
    readTime: "5 min read",
    author: "PrintPartyHub",
  },
  {
    slug: "classroom-word-search-activities",
    title: "How to Use Word Searches in the Classroom",
    description: "Word search puzzles are more than busywork. Here's how teachers use custom word searches for vocabulary review, early finishers, and themed classroom parties.",
    publishedDate: "2026-04-09",
    tags: ["classroom word search", "educational word search"],
    readTime: "6 min read",
    author: "PrintPartyHub",
  },
  {
    slug: "holiday-party-games-printable",
    title: "15 Printable Holiday Party Games for Every Occasion",
    description: "From Christmas office parties to Halloween classroom celebrations, these printable games work for every holiday gathering — and most take under two minutes to prepare.",
    publishedDate: "2026-04-09",
    tags: ["printable party games", "holiday party activities"],
    readTime: "7 min read",
    author: "PrintPartyHub",
  },
  {
    slug: "scavenger-hunt-ideas-kids-birthday",
    title: "The Ultimate Scavenger Hunt Guide for Kids' Birthday Parties",
    description: "Everything you need to run a backyard or indoor scavenger hunt at a kids' birthday party — themes, item lists, rules, and printable cards for every player.",
    publishedDate: "2026-04-09",
    tags: ["scavenger hunt birthday party", "kids scavenger hunt"],
    readTime: "8 min read",
    author: "PrintPartyHub",
  },
];

/** Sorted view used by the index page, sitemap, and RSS feed. Newest first. */
export const POSTS_SORTED: BlogPost[] = [...POSTS].sort((a, b) =>
  b.publishedDate.localeCompare(a.publishedDate)
);

export function findPost(slug: string): BlogPost | undefined {
  return POSTS.find((p) => p.slug === slug);
}

export function formatPostDate(iso: string): string {
  // 2026-06-15 -> "June 15, 2026"
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${months[m - 1]} ${d}, ${y}`;
}
